import { zodResolver } from '@hookform/resolvers/zod';
import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as z from 'zod';
import useAPI from '../../../hooks/useAPI';
import { PaginatedDataEntity } from '../../../lib/types/Settlement';
import Loader from '../../common/Loader';
import Modal from '../../common/modal/Modal';
import Button from '../../ui/Button';
import FormError from '../../ui/FormError';
import Input from '../../ui/Input';
import TransactionSuccessModal from '../transaction-success-dialog';
import styles from './style.module.scss';
import TokenListContext from '../../../contexts/TokenListContext';

type PayoutModalProps = {
  open: boolean;
  onClose: () => void;
  refetch: () => void;
  data?: PaginatedDataEntity | null;
};

const schema = z.object({
  name: z.string()?.nonempty('Name is required'),
  type: z.string()?.nonempty('Type is required'),
  token: z.string().regex(/^[0-9\b]+$/, 'Token must be a number'),
  fiat: z.string()?.nonempty('Fiat is required'),
  wallet: z.string()?.nonempty('Wallet is required'),
});

const PayoutModal = ({ onClose, open, data, refetch }: PayoutModalProps) => {
  const [isConfirm, setIsConfirm] = useState(false);
  const { tokenList } = useContext(TokenListContext);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: 'all',
    defaultValues: {
      type: 'MANUAL',
    },
  });

  const { refetch: onPayOut, isLoading: isLoadingPayOut } = useAPI({
    url: `/withdrawrequest/update/${data?.id}`,
    method: 'PUT',
    onSuccess: () => {
      setIsConfirm(true);
      reset();
      refetch();
      onClose();
    },
    onError: error => {
      toast.error(error?.response?.data?.message || 'An error occurred');
    },
  });

  useEffect(() => {
    if (data) {
      setValue('wallet', data?.createdBy?.wallet?.[0]?.walletAddress || '');
      setValue('fiat', data?.amount);
      setValue('token', data?.amount);
      setValue('type', data?.transactionsMode?.toUpperCase());
      setValue('name', data?.createdBy?.firstName);
    }
  });

  const { refetch: cancelWithdrawalRequest, isLoading: isCancelling } = useAPI({
    url: `/withdrawrequest/cancel/${data?.id}`,
    method: 'DELETE',
    onSuccess: () => {
      toast.success('Request rejected successfully.');
      refetch(); // refresh parent list
      onClose(); // close modal
      reset(); // clear form
    },
    onError: error => {
      toast.error(error?.response?.data?.message || 'Failed to reject request');
    },
  });

  const onSubmit = (data: FieldValues) => {
    if (tokenList[0]?.tokenId) {
      const payload = {
        type: data?.type,
        amount: data?.fiat,
      };
      onPayOut(payload);
    } else {
      toast.error(
        `You haven't created any tokens in the Token Management module. Please generate the required tokens before approving user requests.`,
      );
    }
  };

  return (
    <>
      <TransactionSuccessModal
        open={isConfirm}
        titleClassName={styles.title}
        title="Payout completed Successfully!!"
        body={
          <p className={styles.txnInformation}>
            {moment().format('D MMM YYYY HH:mm:ss')}
          </p>
        }
        isCloseIconVisible={true}
        onClose={() => setIsConfirm(false)}
      />
      <Modal
        open={open}
        title="Payout"
        onClose={() => {
          onClose();
          reset();
        }}
        dialogClassName={styles.dialog_content}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.payoutWrapper}>
            <Input
              label="Client Name"
              placeholder="Enter Client Name"
              {...register('name')}
              isRequired={true}
              error={errors.name?.message as string}
              disabled
            />
            <Form.Group className={styles.selectGroup}>
              <Form.Label className={styles.selectLabel}>
                Change Settlement Type{' '}
                <span className={styles.required}>*</span>
              </Form.Label>
              <Form.Select
                className={styles.selectBox}
                {...register('type')}
                disabled
              >
                <option value="MANUAL">Manual</option>
                <option value="AUTO">Automatic</option>
              </Form.Select>
              {errors.type && (
                <FormError message={errors.type.message as string} />
              )}
            </Form.Group>
            <Input
              label="Redeemed Tokens"
              placeholder="5000"
              isRequired={true}
              {...register('token')}
              disabled
              error={errors.token?.message as string}
            />
            <Input
              label="Fiat"
              placeholder="Enter Fiat"
              isRequired={true}
              {...register('fiat')}
              disabled
              error={errors.fiat?.message as string}
            />
            <Input
              label="Client Wallet Address"
              placeholder="WSK38289303JDK28920DDDYDYUIWH72990002"
              isRequired={true}
              {...register('wallet')}
              error={errors.wallet?.message as string}
              disabled
            />
          </div>

          <div className={styles.actions}>
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                if (data?.id) {
                  cancelWithdrawalRequest();
                } else {
                  toast.error('Invalid request ID.');
                }
              }}
            >
              {isCancelling && <Loader />}
              Reject
            </Button>
            <Button variant="dark" type="submit" className={styles.delete_btn}>
              {isLoadingPayOut && <Loader />}
              Payout Now
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default PayoutModal;
